import { useCallback, useEffect, useEffectEvent, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import Box from "ds/components/Box";
import LoadingIndicator from "ds/components/LoadingIndicator";
import useErrorHandle from "hooks/useErrorHandle";
import useSearchChatConversations from "shared/ChatConversation/useSearchChatConversations";
import useSearchAiIntegrations from "shared/AiIntegration/useSearchAiIntegrations";
import useIntelligenceSettings from "shared/Intelligence/useIntelligenceSettings";
import {
  ArrowCircle,
  ErrorCircleColored,
  LogoSpaceliftColored,
  Plus,
} from "components/icons/generated";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import EmptyState from "ds/components/EmptyState";
import { showBlockNavigationModal } from "hooks/useBlockNavigationModal/BlockNavigationModal";
// Imported via path-mapped specifier (not relative) so Storybook can swap it
// for a mock through the resolve.alias in .storybook/main.ts.
import useIntentMcpClient from "components/AssistantPanel/Chat/hooks/useIntentMcpClient";

import useAssistantPanelStore from "../Layout/useStore";
import useIsAssistantPageMode from "../hooks/useIsAssistantPageMode";
import ChatBudgetExhaustedBanner from "./BudgetExhaustedBanner";
import ChatBudgetProgress from "./BudgetProgress";
import AssistantPanelChatContent from "./Content";
import AssistantPanelHistorySidePanel from "./History/SidePanel";
import ChatApprovalModeSelect from "./ApprovalModeSelect";
import ChatIntegrationSelect from "./IntegrationSelect";
import useChatFrontendTools from "./tools";
import useChatStore from "./store/useStore";
import ChatModeSwitcher from "./ModeSwitcher";
import AssistantPanelChatSimpleHeader from "./SimpleHeader";
import useAssistantPanelChatNavigation from "./hooks/useChatNavigation";
import { showAssistantPanelChatDeleteConfirmationModal } from "./DeleteConfirmationModal";
import { isChatStatusBusy } from "./helpers";
import {
  STOP_RUNNING_CHAT_CONFIRMATION_LABEL,
  STOP_RUNNING_CHAT_DESCRIPTION,
  STOP_RUNNING_CHAT_TITLE,
} from "./constants";

const confirmStopRunningChat = (onConfirm: () => void) => {
  if (!isChatStatusBusy(useChatStore.getState().chat?.status)) {
    onConfirm();
    return;
  }

  showBlockNavigationModal({
    title: STOP_RUNNING_CHAT_TITLE,
    description: STOP_RUNNING_CHAT_DESCRIPTION,
    confirmationButtonLabel: STOP_RUNNING_CHAT_CONFIRMATION_LABEL,
    onConfirm,
  });
};

const AssistantPanelChat = () => {
  const {
    canUseInfraAssistant,
    loading: intelligenceSettingsLoading,
    error: intelligenceSettingsError,
  } = useIntelligenceSettings({ refreshOnMount: true });
  const { toolIcons: frontendToolIcons } = useChatFrontendTools();
  const { toolIcons: mcpToolIcons, isLoading: mcpLoading, error: mcpError } = useIntentMcpClient();

  const {
    integrations,
    initialLoading: integrationLoading,
    error: integrationError,
  } = useSearchAiIntegrations();

  const selectableIntegrations = useMemo(
    () => integrations.filter((i) => i.enabled && i.modelDetails.length > 0),
    [integrations]
  );

  const defaultIntegration = useMemo(
    () => selectableIntegrations.find((i) => i.isSpaceliftProvided) ?? selectableIntegrations[0],
    [selectableIntegrations]
  );

  const { isPanelOpen, setPanelOpen } = useAssistantPanelStore(
    useShallow((state) => ({ isPanelOpen: state.isOpen, setPanelOpen: state.setIsOpen }))
  );
  const isAssistantPageMode = useIsAssistantPageMode();

  const {
    conversations,
    loading: conversationsLoading,
    refetch: refetchConversations,
  } = useSearchChatConversations({ shouldPoll: () => isPanelOpen || isAssistantPageMode });

  const IntegrationErrorContent = useErrorHandle(integrationError);

  const { conversationError, setIntegrationId, setModel, integrationId, model } = useChatStore(
    useShallow((state) => ({
      conversationError: state.conversationError,
      setIntegrationId: state.setIntegrationId,
      setModel: state.setModel,
      integrationId: state.integrationId,
      model: state.model,
    }))
  );

  const toolIcons = useMemo(
    () => ({ ...frontendToolIcons, ...mcpToolIcons }),
    [frontendToolIcons, mcpToolIcons]
  );

  const {
    routeChatId,
    conversationLoading,
    handleFirstMessage,
    handleNewChat,
    handleDeleteChat,
    handleSelectChat,
    handleReloadChat,
  } = useAssistantPanelChatNavigation({
    defaultIntegration,
    integrationLoading,
    refetchConversations,
  });

  const handleDeleteChatWithConfirmation = useCallback(
    (chatId: string) => {
      showAssistantPanelChatDeleteConfirmationModal({
        name: conversations.find((conversation) => conversation.id === chatId)?.name ?? "this chat",
        onConfirm: () => handleDeleteChat(chatId),
      });
    },
    [conversations, handleDeleteChat]
  );

  const handleNewChatWithConfirmation = useCallback(
    () => confirmStopRunningChat(handleNewChat),
    [handleNewChat]
  );

  const handleSelectChatWithConfirmation = useCallback(
    (chatId: string) => {
      if (chatId !== useChatStore.getState().chat?.id) {
        confirmStopRunningChat(() => handleSelectChat(chatId));
      }
    },
    [handleSelectChat]
  );

  const canShowBudgetProgress = useMemo(
    () => integrations.find((i) => i.id === integrationId)?.tokenUsage?.totalTokens !== -1,
    [integrations, integrationId]
  );

  const setDefaultIntegrationAndModel = useEffectEvent(() => {
    const defaultModelId = defaultIntegration?.modelDetails[0]?.id;

    if (!defaultIntegration?.id || !defaultModelId) {
      return;
    }

    const { integrationId: currentId, model: currentModel } = useChatStore.getState();
    if (currentId === defaultIntegration.id && currentModel === defaultModelId) {
      return;
    }

    setIntegrationId(defaultIntegration.id);
    setModel(defaultModelId);
  });

  useEffect(() => {
    if (integrationLoading) {
      return;
    }

    const isValid =
      integrationId &&
      model &&
      integrations.some((i) =>
        i.id === integrationId ? i.enabled && i.modelDetails.some((m) => m.id === model) : false
      );

    if (!isValid) {
      setDefaultIntegrationAndModel();
    }
  }, [integrationId, integrationLoading, integrations, model]);

  const showTermsOverlay =
    !intelligenceSettingsLoading && !intelligenceSettingsError && !canUseInfraAssistant;

  if (mcpLoading || integrationLoading) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <LoadingIndicator loading={true} />
        </Box>
      </Box>
    );
  }

  if (mcpError) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <EmptyState
            icon={ErrorCircleColored}
            title="An error occurred, please try refreshing the app."
            caption="If the error persists, contact support."
          >
            <Button
              variant="secondary"
              startIcon={ArrowCircle}
              size="small"
              onPress={() => window.location.reload()}
            >
              Refresh page
            </Button>
          </EmptyState>
        </Box>
      </Box>
    );
  }

  if (IntegrationErrorContent) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          {IntegrationErrorContent}
        </Box>
      </Box>
    );
  }

  if (!defaultIntegration && !showTermsOverlay) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <EmptyState icon={LogoSpaceliftColored} title="No AI integration available">
            <LinkButton
              to="/integrations/ai"
              variant="secondary"
              size="small"
              onClick={() => {
                if (!isAssistantPageMode) {
                  setPanelOpen(false);
                }
              }}
            >
              Set up an AI integration
            </LinkButton>
          </EmptyState>
        </Box>
      </Box>
    );
  }

  if (conversationError) {
    return (
      <Box direction="column" fullHeight>
        <AssistantPanelChatSimpleHeader />
        <Box direction="column" grow="1" justify="center" align="center">
          <EmptyState
            icon={ErrorCircleColored}
            title={conversationError}
            caption={
              routeChatId ? "It may have been deleted, or you don't have access to it." : undefined
            }
          >
            <Button variant="secondary" size="small" startIcon={Plus} onPress={handleNewChat}>
              Start a new chat
            </Button>
          </EmptyState>
        </Box>
      </Box>
    );
  }

  return (
    <Box direction="row" fullHeight>
      {!showTermsOverlay && (
        <AssistantPanelHistorySidePanel
          deleteChat={handleDeleteChatWithConfirmation}
          chats={conversations}
          loading={conversationsLoading}
          onSelectChat={handleSelectChatWithConfirmation}
        />
      )}
      <Box direction="column" fullHeight relative grow="1" zeroMinWidth>
        <AssistantPanelChatContent
          toolIcons={toolIcons}
          onFirstMessage={handleFirstMessage}
          onNewChat={handleNewChatWithConfirmation}
          conversations={conversations}
          conversationsLoading={conversationsLoading}
          conversationLoading={conversationLoading}
          onSelectChat={handleSelectChatWithConfirmation}
          onDeleteChat={handleDeleteChatWithConfirmation}
          onRefetchConversations={refetchConversations}
          onReloadChat={handleReloadChat}
          beforeComposer={<ChatBudgetExhaustedBanner />}
          showTermsOverlay={showTermsOverlay}
          composerToolbarSlot={
            <Box align="center" shrink="0">
              <ChatModeSwitcher />
            </Box>
          }
          composerFooterSlot={
            <Box align="center" padding="0 large" gap="medium" justify="between" fullWidth>
              <Box align="center" shrink="0">
                <ChatApprovalModeSelect />
              </Box>
              <Box zeroMinWidth gap="medium" align="center">
                <ChatIntegrationSelect />
                {canShowBudgetProgress && <ChatBudgetProgress />}
              </Box>
            </Box>
          }
        />
      </Box>
    </Box>
  );
};

export default AssistantPanelChat;
